import { Dropdown, IDropdownOption, Stack } from '@fluentui/react';
import { Calendar20Regular, CalendarDay20Regular, CalendarMonth20Regular, CalendarWeekStart20Regular, CalendarWorkWeek20Regular } from '@fluentui/react-icons';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import shallow from 'zustand/shallow';
import { useTheme } from '../../Themes/themeContext';
import { DateResolution } from '../../api/generated/data-contracts';
import { useUISettings } from '../../context/network/http/QueryProvider/queries/UISettings';
import { useCurrentProject } from '../../context/network/http/QueryProvider/queries/project';
import { useStore } from '../../context/store';
import { CellRender, PickerMode, RangePicker, RangePickerProps } from './components/RangePicker';
import { getDefaultEndDate, getEndOf, getStartOf, getToday } from './helpers';
import { DateRange } from './types';
import { useTimeline } from './useTimeline';
import { CalendarCell } from './components/CalendarCell';
import { TodayButton } from './components/TodayButton';
import { useMinMaxDates } from './hooks/useMinMaxDates';
import { useTranslation } from '../../Locales/i18n/localizationService';

export const PeriodPicker = memo(function PeriodPicker({ children }: { children?: React.ReactNode }) {
    const { t } = useTranslation();
    const { data: uiSettings } = useUISettings();
    const { currentResolution, start, end, setTimeline } = useTimeline();

    useGetProjectMetadata();

    const options = useMemo(() => {
        const allowedResolutions = uiSettings.settings.allowedTimelineResolutions || [];
        return [
            allowedResolutions.includes(DateResolution.Year) && {
                key: DateResolution.Year,
                text: t('Common.Year'),
                data: 'year',
            },
            allowedResolutions.includes(DateResolution.Quarter) && {
                key: DateResolution.Quarter,
                text: t('Common.Quarter'),
                data: 'quarter',
            },

            allowedResolutions.includes(DateResolution.Month) && {
                key: DateResolution.Month,
                text: t('Common.Month'),
                data: 'month',
            },

            allowedResolutions.includes(DateResolution.Week) && {
                key: DateResolution.Week,
                text: t('Common.Week'),
                data: 'week',
            },

            allowedResolutions.includes(DateResolution.Day) && {
                key: DateResolution.Day,
                text: t('Common.Day'),
                data: 'date',
            },
        ].filter(Boolean) as IDropdownOption<PickerMode>[];
    }, [uiSettings.settings.allowedTimelineResolutions, t]);

    const getMinMax = useMinMaxDates();
    const [minDate, maxDate] = useMemo(() => getMinMax(currentResolution), [currentResolution, getMinMax]);

    const onChangeResolution = useCallback(
        (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<PickerMode>) => {
            if (option) {
                const resolution = option.key as DateResolution;
				const start = getStartOf(resolution, getToday());
				const end = getDefaultEndDate(resolution, start, uiSettings.settings.periodPickerSettings)
                setTimeline({
                    resolution,
                    start,
                    end,
                });
            }
        },
        [setTimeline, uiSettings.settings.periodPickerSettings],
    );

    const pickerMode = useMemo(() => options.find(option => option.key === currentResolution)?.data as PickerMode, [currentResolution, options]);

    const onChange = useCallback<RangePickerProps['onChange']>(
        ([start, end]) => {
			const startDate = getStartOf(currentResolution, start);
			const endDate = getEndOf(currentResolution, end);
            setTimeline({
                start: startDate,
                end: endDate,
            });
        },
        [currentResolution, setTimeline],
    );

    const theme = useTheme();

    const rangePickerValue = useMemo((): DateRange => {
        return [start, end];
    }, [start, end]);

    const rangePickerStyle = useMemo(() => {
        return {
            border: `1px solid ${theme.semanticColors.inputBorder}`,
        };
    }, [theme.semanticColors.inputBorder]);

    return (
        <Stack horizontal verticalAlign="center" styles={{ root: { padding: 10, gap: 10, backgroundColor: 'white', margin: '10px 8px' } }}>
            <Dropdown
                //
                options={options}
                selectedKey={currentResolution}
                onChange={onChangeResolution}
                onRenderOption={onRenderOption}
                onRenderTitle={onRenderTitle}
                styles={{ root: { width: 120 } }}
            />
            <RangePicker
                //
                picker={pickerMode}
                allowClear={false}
                minDate={minDate}
                maxDate={maxDate}
                onChange={onChange}
                allowEmpty={false}
                value={rangePickerValue}
                style={rangePickerStyle}
                suffixIcon={<CalendarIcon resolution={currentResolution} />}
                cellRender={cellRender}
                showNow
                showWeek
                inputReadOnly
            />
            <TodayButton />
            {children}
        </Stack>
    );
});

const cellRender: CellRender = (date, info) => {
    return <CalendarCell info={info} date={date as Date} />;
};

const CalendarIcon = ({ resolution }: { resolution: DateResolution }) => {
    const Icon = useMemo(() => {
        switch (resolution) {
            case DateResolution.Day:
                return CalendarDay20Regular;
            case DateResolution.Quarter:
                return CalendarWorkWeek20Regular;
            case DateResolution.Week:
                return CalendarWeekStart20Regular;
            case DateResolution.Year:
                return Calendar20Regular;
            case DateResolution.Month:
            default: {
                return CalendarMonth20Regular;
            }
        }
    }, [resolution]);
    return <Icon />;
};

const Option = ({ option }: { option: IDropdownOption<PickerMode> }) => {
    return (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
            <CalendarIcon resolution={option.key as DateResolution} />
            <span>{option.text}</span>
        </Stack>
    );
};

const onRenderOption = (option: IDropdownOption<PickerMode>) => {
    return <Option option={option} />;
};

const onRenderTitle = ([option]: IDropdownOption<PickerMode>[]) => {
    return <Option option={option} />;
};

const useGetProjectMetadata = () => {
    const { projectId } = useParams();
    const setTimelineMeta = useStore(store => store.setTimelineMeta, shallow);
    const { data } = useCurrentProject(projectId, true);
    useEffect(() => {
        if (data) {
            setTimelineMeta({ end: data.end, start: data.start, name: data.name });
            return () => {
                setTimelineMeta(null);
            };
        }
    }, [data, setTimelineMeta]);
};
